import React, { useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Typography,
  Box,
} from '@mui/material';
import SortableHeaderCell from './SortableHeaderCell';

/**
 * Generic data table with pagination and optional client-side multi-column sort.
 * Replaces the repeated pattern of Table + TableHead + TableBody + TablePagination
 * found in StakeholderManagement (3x), ApprovalsPage, ReferenceDataManagement, etc.
 *
 * @param {object} props
 * @param {Array<{ key, header, render?, align?, sx?, sortable?, getValue? }>} props.columns
 *   Column definitions.
 *   - `render(row)` returns cell content; if omitted, uses `row[key]`.
 *   - `sortable: true` opts the column into sort (only meaningful when `sort` prop is passed).
 *   - `getValue(row)` extracts the comparable value for client-side sorting; defaults to `row[key]`.
 * @param {Array} props.data - Full data array (sorted then paginated internally if hooks provided)
 * @param {object} [props.pagination] - Result from usePagination() hook
 * @param {object} [props.sort] - Result from useTableSort() hook (enables sortable columns)
 * @param {string} [props.emptyMessage='No data found']
 * @param {string} [props.keyField='id']
 * @param {Function} [props.onRowClick]
 * @param {object} [props.tableContainerSx]
 * @param {boolean} [props.dense=false]
 * @param {boolean} [props.stickyHeader=false]
 */
const DataTable = ({
  columns,
  data = [],
  pagination,
  sort,
  emptyMessage = 'No data found',
  keyField = 'id',
  onRowClick,
  tableContainerSx,
  dense = false,
  stickyHeader = false,
}) => {
  const sortedData = useMemo(
    () => (sort ? sort.sortRows(data, columns) : data),
    [sort, data, columns]
  );
  const displayData = pagination ? pagination.paginateData(sortedData) : sortedData;

  return (
    <>
      <TableContainer component={Paper} sx={tableContainerSx}>
        <Table size={dense ? 'small' : 'medium'} stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {columns.map((col) => (
                sort && col.sortable ? (
                  <SortableHeaderCell
                    key={col.key}
                    sort={sort}
                    columnKey={col.key}
                    align={col.align || 'left'}
                    sx={col.sx}
                  >
                    {col.header}
                  </SortableHeaderCell>
                ) : (
                  <TableCell key={col.key} align={col.align || 'left'} sx={col.sx}>
                    {col.header}
                  </TableCell>
                )
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} align="center">
                  <Box sx={{ py: 3 }}>
                    <Typography variant="body2" color="text.secondary">
                      {emptyMessage}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              displayData.map((row, index) => (
                <TableRow
                  key={row[keyField] ?? index}
                  hover
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={onRowClick ? { cursor: 'pointer' } : undefined}
                >
                  {columns.map((col) => (
                    <TableCell key={col.key} align={col.align || 'left'}>
                      {col.render ? col.render(row) : row[col.key]}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {pagination && (
        <TablePagination {...pagination.tableProps(sortedData.length)} />
      )}
    </>
  );
};

export default DataTable;
